import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Chip from 'material-ui/Chip';

export const styles = theme => ({
  root: {
    position: 'relative',
  },

  container: {
    borderRadius: 2,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #ced4da',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    display: 'flex',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: '#1A65FF',
    },
    width: '100%',
    paddingBottom: '5px',
    height: '80px',
    overflowY: 'scroll',
    overflowX: 'hidden',
  },

  chipContainer: {
    display: 'block',
    paddingLeft: '10px',
    paddingRight: '5px',
  },

  textRoot: {
    display: 'inline-flex',
    marginTop: 0,
    fontSize: '13px',
    float: 'left',
    minWidth: '50px',
    padding: '8px 12px 8px 0px',
    border: 'none',
    appearance: 'none', // Remove border in Safari, doesn't seem to break anything in other browsers
    '&:focus': {
      border: 'none',
      appearance: 'none', // Remove border in Safari, doesn't seem to break anything in other browsers
      outline: 'none',
    }
  },

  chipRoot: {
    marginRight: '5px',
    marginTop: '5px',
    float: 'left',
    backgroundColor: 'rgba(26,101,255,.05)',
    border: '#1A65FF solid 1px',
    color: '#1A65FF',
    height: 'unset',
  },

  chipLabel: {
    padding: '2px 8px',
    fontSize: '11px',
    whiteSpace: 'normal',
  },

  dropdown: {
    backgroundColor: theme.palette.common.white,
    position: 'absolute',
    width: '100%',
    boxSizing: 'border-box',
    borderColor: '#ced4da',
    borderStyle: 'solid',
    borderWidth: '0px 1px 1px 1px',
    padding: '10px',
    fontSize: '11px',
    overflow: 'hidden',
  },
});

class ChipTextarea extends React.Component {
  state = {
    focusedChip: null,
    inputValue: '',
    isFocused: false,
    showDropdown: false,
  }

  handleAddChip = (chip) => {
    this.setState({ inputValue: '' })
    const chips = this.props.chips;

    if (chip === '') return;

    if (this.props.allowDuplicates || chips.indexOf(chip) === -1) {
      if (this.props.chips && this.props.onAdd) {
        this.props.onAdd(chip)
      } else {
        if (this.props.onChange) {
          this.props.onChange([...this.props.chips, chip])
        }
      }
    }
  }

  handleDeleteChip = (chip, i) => {
    const chips = [...this.props.chips];
    const changed = chips.splice(i, 1) // remove the chip at index i
    if (changed) {
      let focusedChip = this.state.focusedChip;

      if (this.state.focusedChip === i) {
        focusedChip = null
      } else if (this.state.focusedChip > i) {
        focusedChip = this.state.focusedChip - 1
      }

      this.setState({ focusedChip })

      if (this.props.onChange) {
        this.props.onChange(chips)
      }
    }
  }

  handleInputChange = (e) => {
    this.setState({
      inputValue: e.target.value,
      showDropdown: e.target.value && e.target.value !== '',
    })

    if (this.props.onUpdateInput) {
      this.props.onUpdateInput(e)
    }
  }

  handleKeyDown = (e) => {
    const { focusedChip } = this.state;
    const { chips } = this.props;

    const enterKeyCode = 13;
    const backspaceKeyCode = 8;
    const deleteKeyCode = 46;
    const leftArrowKeyCode = 37;
    const rightArrowKeyCode = 39;

    switch (e.keyCode) {
      case enterKeyCode: //Enter key
        this.handleAddChip(this.state.inputValue);
        this.clearInput();
        break;
      case leftArrowKeyCode: //back button
        if (focusedChip == null && e.target.value === '' && chips && chips.length) {
          return this.setState({ focusedChip: chips.length - 1 })
        }
        if (focusedChip != null && focusedChip > 0) {
          this.setState({ focusedChip: focusedChip - 1 })
        }
        break;
      case rightArrowKeyCode: //forward button
        if (focusedChip != null && focusedChip < chips.length - 1) {
          this.setState({ focusedChip: focusedChip + 1 })
        } else {
          this.setState({ focusedChip: null })
        }
        break;
      case backspaceKeyCode: //delete button
      case deleteKeyCode:
        if (e.target.value === '') { // when inputValue is empty
          this.clearInput();
          if (focusedChip == null) {
            this.setState({ focusedChip: chips.length - 1 })
          } else {
            const value = chips[focusedChip]
            this.handleDeleteChip(value, focusedChip)
          }
        }
        break;
      default:
        this.setState({ focusedChip: null });
        break;
    }
  }

  handleInputFocus = () => {
    this.setState({
      isFocused: true,
      focusedChip: null,
    });
  }

  handleInputBlur = () => {
    this.setState({
      isFocused: false,
      focusedChip: null,
    });

    if (this.state.inputValue !== '') {
      this.handleAddChip(this.state.inputValue);
    }
  }

  clearInput = () => {
    this.setState({
      inputValue: '',
      showDropdown: false,
    });
  }

  setInput = (node) => {
    if (node) {
      this.input = node;
    }
  }

  renderChip = ({ text, isFocused, isDisabled, handleClick }, key) => (
    <Chip
      key={key}
      color="primary"
      style={{
        pointerEvents: isDisabled ? 'none' : undefined,
        backgroundColor: isFocused ? 'rgba(26,101,255,.7)' : 'rgba(26,101,255,.05)',
        color: isFocused ? '#FFFFFF' : '#1A65FF',
      }}
      classes={{
        root: this.props.classes.chipRoot,
        label: this.props.classes.chipLabel,
      }}
      onClick={handleClick}
      label={text}
    />
  );

  render() {
    const {
      classes,
      disabled,
      chips,
      onChange: _onChange,
      id,
      placeholder,
      InputProps,
      ...rest
    } = this.props;

    const { showDropdown } = this.state;

    const hasInput = chips.length > 0 || this.state.inputValue.length > 0

    return (
      <div className={classes.root} onClick={() => {
        if (this.input) {
          this.input.focus();
        }
      }} {...rest}>
        <div
          className={classes.container}
          style={{
            borderColor: this.state.isFocused ? '#1A65FF' : '#ced4da'
          }}
        >
          <div className={classes.chipContainer}>
            {chips.map((tag, i) => {
              return this.renderChip({
                text: tag,
                isFocused: this.state.focusedChip === i,
                isDisabled: disabled,
                handleClick: (e) => {
                  e.stopPropagation();
                  this.setState({ focusedChip: this.state.focusedChip === i ? null : i }
                  )
                },
                handleDelete: () => this.handleDeleteChip(tag, i),
                className: classes.chip
              }, i)
            })}
            <input
              id={id}
              className={classes.textRoot}
              value={this.state.inputValue}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              disabled={disabled}
              placeholder={!hasInput ? placeholder : null}
              ref={this.setInput}
              {...InputProps}
            />
          </div>
        </div>
        <div className={classes.dropdown} style={{ display: showDropdown ? 'block' : 'none' }}>
          Enterを押して<b>{this.state.inputValue}</b>を追加
        </div>
      </div>
    );
  }
}

ChipTextarea.propTypes = {
  classes: PropTypes.object.isRequired,
  chips: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  allowDuplicates: PropTypes.bool,
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  onUpdateInput: PropTypes.func,
  id: PropTypes.string,
  InputProps: PropTypes.object,
}

ChipTextarea.defaultValue = {
  chips: [],
  disabled: false,
  placeholder: '',
  allowDuplicates: true,
}
export default withStyles(styles)(ChipTextarea);



// WEBPACK FOOTER //
// ./src/components/common/ChipTextarea.jsx